/**
 * ServicesManagerView — Plano 2 (setup) peça 2: gestão do catálogo de SERVIÇOS da agenda.
 *
 * O serviço define o que um agendamento consome: duração, buffers (limpeza/preparo entre
 * atendimentos), quais TIPOS de recurso exige (serviço composto sala+profissional), lead-time
 * mínimo, janela de cancelamento, fila de espera e um pré-requisito a qualificar antes de
 * ofertar horário (intakeNote — "convênio ou particular?"). Casos: barbearia "corte"/"barba"
 * (durações diferentes); clínica "consulta" [sala, profissional]; massagista "massagem 60min".
 *
 * Gated `scheduling-native` (na página). CRUD sobre /v1/scheduling/services: criar/editar
 * (If-Match) e aposentar (soft-delete — preserva o histórico via title do agendamento).
 */
import { Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';

import { useServiceActions } from '../../hooks/use-scheduling';
import { ApiError, type SchedulingServiceWire } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import { RESOURCE_TYPE_ORDER, resourceTypeLabel, type ResourceType } from './resource-labels';

import type { JSX, ReactNode } from 'react';

export interface ServicesManagerViewProps {
  services: SchedulingServiceWire[];
  canManage: boolean;
}

type FormState = { mode: 'create' } | { mode: 'edit'; service: SchedulingServiceWire } | null;

function toast(variant: 'success' | 'error' | 'info', title: string, description?: string): void {
  useToastStore.getState().push({
    variant,
    title,
    ...(description !== undefined && { description }),
    durationMs: variant === 'error' ? 6000 : 3500,
  });
}

/** Chip neutro (tipo de recurso exigido) / badge (fila/pré-requisito) do serviço. */
function Chip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
      style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}
    >
      {children}
    </span>
  );
}

function AiBadge({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-xs"
      style={{ background: 'var(--ai-bg)', border: '1px solid var(--ai-border)', color: 'var(--ai-fg)' }}
    >
      {children}
    </span>
  );
}

/** Metadados do serviço: duração (badge âmbar) + tipos de recurso exigidos + fila/pré-requisito. */
function ServiceMeta({ service }: { service: SchedulingServiceWire }): JSX.Element {
  const hasBuffer = service.bufferBeforeMin > 0 || service.bufferAfterMin > 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold"
        style={{ background: 'var(--brand-subtle)', color: 'var(--brand-subtle-fg)' }}
      >
        {service.defaultDurationMinutes} min
      </span>
      {service.requiredResourceTypes.map((t) => (
        <Chip key={t}>{resourceTypeLabel(t as ResourceType)}</Chip>
      ))}
      {hasBuffer && (
        <Chip>
          buffer {service.bufferBeforeMin}/{service.bufferAfterMin} min
        </Chip>
      )}
      {service.waitlistEnabled && <AiBadge>fila de espera</AiBadge>}
      {service.intakeNote !== null && service.intakeNote.trim() !== '' && (
        <AiBadge>pré-requisito</AiBadge>
      )}
    </div>
  );
}

export function ServicesManagerView({ services, canManage }: ServicesManagerViewProps): JSX.Element {
  const [form, setForm] = useState<FormState>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const actions = useServiceActions();

  const handleDeactivate = (service: SchedulingServiceWire): void => {
    actions.deactivate.mutate(
      { id: service.id, version: service.version },
      {
        onSuccess: () => {
          setConfirmingId(null);
          toast('success', `"${service.name}" aposentado`, 'Some do catálogo; o histórico é preservado.');
        },
        onError: (err) => {
          setConfirmingId(null);
          if (err instanceof ApiError && err.status === 409) {
            toast('info', 'A lista foi atualizada por outra alteração', 'Recarregamos os serviços. Tente novamente.');
            return;
          }
          toast('error', 'Não foi possível aposentar o serviço.');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div className="flex items-center">
        <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
          {services.length === 0
            ? 'Nenhum serviço cadastrado ainda.'
            : `${services.length.toString()} ${services.length === 1 ? 'serviço' : 'serviços'} no catálogo`}
        </p>
        {canManage && (
          <button type="button" className="btn btn--primary ml-auto" onClick={() => setForm({ mode: 'create' })}>
            <Plus size={14} aria-hidden /> Novo serviço
          </button>
        )}
      </div>

      <div
        className="rounded-md px-4 py-3 text-sm"
        role="note"
        style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}
      >
        O serviço define a duração, os buffers de preparo e quais <strong>tipos de recurso</strong> um
        agendamento exige — um serviço pode pedir sala <em>e</em> profissional ao mesmo tempo. A IA usa
        essa configuração para oferecer horários corretos.
      </div>

      {services.length === 0 && (
        <div
          className="rounded-md px-4 py-8 text-center text-sm"
          style={{ border: '1px dashed var(--border-default)', color: 'var(--fg-3)' }}
        >
          Cadastre o primeiro serviço para a agenda saber o que oferecer.
        </div>
      )}

      {services.length > 0 && (
        <ul className="flex flex-col gap-2">
          {services.map((service) => (
            <li
              key={service.id}
              className="flex items-center gap-3 rounded-md px-4 py-3"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}
            >
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="truncate font-medium" style={{ color: 'var(--fg-1)' }} title={service.id}>
                  {service.name}
                </span>
                <ServiceMeta service={service} />
              </div>
              {canManage && (
                <div className="ml-auto flex items-center gap-2">
                  {confirmingId === service.id ? (
                    <>
                      <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                        Aposentar?
                      </span>
                      <button
                        type="button"
                        className="btn btn--destructive btn--sm"
                        disabled={actions.deactivate.isPending}
                        onClick={() => handleDeactivate(service)}
                      >
                        Confirmar
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={actions.deactivate.isPending}
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        aria-label={`Editar ${service.name}`}
                        onClick={() => setForm({ mode: 'edit', service })}
                      >
                        <Pencil size={14} aria-hidden /> Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        aria-label={`Aposentar ${service.name}`}
                        onClick={() => setConfirmingId(service.id)}
                      >
                        Aposentar
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {form !== null && (
        <ServiceFormModal
          mode={form.mode}
          {...(form.mode === 'edit' && { service: form.service })}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

interface ServiceFormModalProps {
  mode: 'create' | 'edit';
  service?: SchedulingServiceWire;
  onClose: () => void;
}

function ServiceFormModal({ mode, service, onClose }: ServiceFormModalProps): JSX.Element {
  const actions = useServiceActions();
  const [name, setName] = useState(service?.name ?? '');
  const [duration, setDuration] = useState(String(service?.defaultDurationMinutes ?? 30));
  const [bufferBefore, setBufferBefore] = useState(String(service?.bufferBeforeMin ?? 0));
  const [bufferAfter, setBufferAfter] = useState(String(service?.bufferAfterMin ?? 0));
  const [types, setTypes] = useState<ResourceType[]>((service?.requiredResourceTypes ?? []) as ResourceType[]);
  const [leadTime, setLeadTime] = useState(service?.minLeadTimeMin !== undefined && service.minLeadTimeMin !== null ? String(service.minLeadTimeMin) : '');
  const [waitlist, setWaitlist] = useState(service?.waitlistEnabled ?? false);
  const [intakeNote, setIntakeNote] = useState(service?.intakeNote ?? '');

  const durationNum = Number.parseInt(duration, 10);
  const valid = name.trim().length > 0 && Number.isInteger(durationNum) && durationNum >= 1;
  const busy = actions.create.isPending || actions.update.isPending;

  const toggleType = (t: ResourceType): void => {
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const handle409 = (err: unknown): boolean => {
    if (err instanceof ApiError && err.status === 409) {
      toast('info', 'A lista foi atualizada por outra alteração', 'Recarregamos os serviços. Reabra e tente novamente.');
      onClose();
      return true;
    }
    return false;
  };

  const onSubmit = (): void => {
    if (!valid || busy) return;
    const parseNonNeg = (v: string): number => {
      const n = Number.parseInt(v, 10);
      return Number.isNaN(n) || n < 0 ? 0 : n;
    };
    const leadNum = leadTime.trim() === '' ? undefined : Number.parseInt(leadTime, 10);
    const body = {
      name: name.trim(),
      defaultDurationMinutes: durationNum,
      bufferBeforeMin: parseNonNeg(bufferBefore),
      bufferAfterMin: parseNonNeg(bufferAfter),
      requiredResourceTypes: types,
      waitlistEnabled: waitlist,
      intakeNote: intakeNote.trim(),
      ...(leadNum !== undefined && Number.isInteger(leadNum) && leadNum >= 0 && { minLeadTimeMin: leadNum }),
    };
    if (mode === 'create') {
      actions.create.mutate(body, {
        onSuccess: () => {
          toast('success', `"${body.name}" cadastrado`);
          onClose();
        },
        onError: (err) => {
          if (!handle409(err)) toast('error', 'Não foi possível cadastrar o serviço.');
        },
      });
      return;
    }
    if (service === undefined) return;
    actions.update.mutate(
      { id: service.id, version: service.version, ...body },
      {
        onSuccess: () => {
          toast('success', `"${body.name}" atualizado`);
          onClose();
        },
        onError: (err) => {
          if (!handle409(err)) toast('error', 'Não foi possível atualizar o serviço.');
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-label={mode === 'create' ? 'Novo serviço' : `Editar ${service?.name ?? 'serviço'}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg p-6 shadow-xl" style={{ background: 'var(--bg-surface)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Novo serviço' : 'Editar serviço'}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            <X size={14} aria-hidden /> Fechar
          </button>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--fg-3)' }}>
          A duração e os tipos de recurso exigidos definem como a IA oferece horários.
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Nome</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="Ex.: Corte, Massagem 60min, Consulta"
            aria-label="Nome do serviço"
          />
        </label>

        <div className="mb-3 grid grid-cols-3 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Duração (min)</span>
            <input
              type="number"
              min={1}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              aria-label="Duração em minutos"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Buffer antes</span>
            <input
              type="number"
              min={0}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={bufferBefore}
              onChange={(e) => setBufferBefore(e.target.value)}
              aria-label="Buffer antes (min)"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Buffer depois</span>
            <input
              type="number"
              min={0}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={bufferAfter}
              onChange={(e) => setBufferAfter(e.target.value)}
              aria-label="Buffer depois (min)"
            />
          </label>
        </div>

        <fieldset className="mb-3">
          <legend className="mb-1 block text-sm font-medium">Recursos exigidos</legend>
          <div className="flex flex-wrap gap-2">
            {RESOURCE_TYPE_ORDER.map((t) => (
              <label
                key={t}
                className="flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-sm"
                style={{
                  borderColor: types.includes(t) ? 'var(--brand)' : 'var(--border-default)',
                  background: types.includes(t) ? 'var(--brand-subtle)' : 'var(--bg-surface-2)',
                  color: types.includes(t) ? 'var(--brand-subtle-fg)' : 'var(--fg-2)',
                }}
              >
                <input type="checkbox" checked={types.includes(t)} onChange={() => toggleType(t)} aria-label={`Exige ${resourceTypeLabel(t)}`} />
                {resourceTypeLabel(t)}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Antecedência mín. (min)</span>
            <input
              type="number"
              min={0}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={leadTime}
              onChange={(e) => setLeadTime(e.target.value)}
              placeholder="opcional"
              aria-label="Antecedência mínima em minutos"
            />
          </label>
          <label className="flex items-end gap-2 pb-1">
            <input type="checkbox" checked={waitlist} onChange={(e) => setWaitlist(e.target.checked)} aria-label="Habilitar fila de espera" />
            <span className="text-sm font-medium">Fila de espera</span>
          </label>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium">Pré-requisito a perguntar (opcional)</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={intakeNote}
            onChange={(e) => setIntakeNote(e.target.value)}
            maxLength={500}
            placeholder="Ex.: Convênio ou particular? Já tem a guia médica?"
            aria-label="Pré-requisito a perguntar"
          />
        </label>

        <button type="button" className="btn btn--primary w-full" onClick={onSubmit} disabled={!valid || busy}>
          {busy ? 'Salvando…' : mode === 'create' ? 'Cadastrar serviço' : 'Salvar alterações'}
        </button>
      </div>
    </div>
  );
}
